/**
 * Declarative config for the additional-loan overview banner.
 * Add a new modal variant here + handle its action ids in the hook’s `onModalAction`.
 */

export const ADDITIONAL_LOAN_MODAL_VARIANT = {
  NOT_ELIGIBLE: "not_eligible",
  OVERDUE: "overdue",
  ELIGIBLE: "eligible",
};

export const ADDITIONAL_LOAN_IMAGES = {
  runningPerson: "/image/lms/Illustration-running-person.png",
  notEligible: "/image/lms/illustration-not-eligible.png",
  eligible: "/image/lms/illustration-eligible.png",
};

/** Action ids consumed by `useAdditionalLoanOverviewBanner` → `onModalAction` */
export const ADDITIONAL_LOAN_MODAL_ACTION = {
  DISMISS: "dismiss",
  PAY_OVERDUE: "pay_overdue",
  PROCEED: "proceed",
};

/**
 * @typedef {'success' | 'error'} StatusBadgeTone
 * @typedef {{ id: string, tone: 'primary' | 'muted', label: string, labelLoading?: string }} ModalActionDef
 * @typedef {{ statusBadge: StatusBadgeTone, illustrationKey: keyof typeof ADDITIONAL_LOAN_IMAGES, title: string, description: string, actions: ModalActionDef[] }} ModalDefinition
 */

/** @type {Record<string, ModalDefinition>} */
export const ADDITIONAL_LOAN_MODAL_CONFIG = {
  [ADDITIONAL_LOAN_MODAL_VARIANT.NOT_ELIGIBLE]: {
    statusBadge: "error",
    illustrationKey: "notEligible",
    title: "Not eligible yet",
    description:
      "To apply for a new loan, you need to repay at least 60% of your latest active loan.",
    actions: [
      {
        id: ADDITIONAL_LOAN_MODAL_ACTION.DISMISS,
        tone: "primary",
        label: "Close",
      },
    ],
  },
  [ADDITIONAL_LOAN_MODAL_VARIANT.OVERDUE]: {
    statusBadge: "error",
    illustrationKey: "notEligible",
    title: "Overdue payment pending",
    description: "To apply for a new loan, you'll need to clear your overdue instalments first.",
    actions: [
      {
        id: ADDITIONAL_LOAN_MODAL_ACTION.PAY_OVERDUE,
        tone: "primary",
        label: "Pay overdue",
      },
      {
        id: ADDITIONAL_LOAN_MODAL_ACTION.DISMISS,
        tone: "muted",
        label: "Close",
      },
    ],
  },
  [ADDITIONAL_LOAN_MODAL_VARIANT.ELIGIBLE]: {
    statusBadge: "success",
    illustrationKey: "eligible",
    title: "Good news, you're eligible",
    description: "You can now proceed with your new loan application.",
    actions: [
      {
        id: ADDITIONAL_LOAN_MODAL_ACTION.PROCEED,
        tone: "primary",
        label: "Proceed",
        labelLoading: "Please wait…",
      },
      {
        id: ADDITIONAL_LOAN_MODAL_ACTION.DISMISS,
        tone: "muted",
        label: "Close",
      },
    ],
  },
};

export const ADDITIONAL_LOAN_BANNER = {
  headline: "Need extra funds?",
  gradient: "linear-gradient(90deg, #C5EEF5 0%, #F0F9FB 50%, #FFFFFF 100%)",
  copy: {
    newApplication: {
      subheadline: "Apply for a new loan.",
      cta: "Apply Now!",
    },
    inProgress: {
      subheadline: "Complete your loan application",
      cta: "Complete now!",
    },
  },
};

/**
 * Decide whether the banner CTA should navigate immediately or open a modal.
 * Order matters: child loan → direct navigation; then overdue; then eligibility.
 */
export function resolveBannerCtaIntent({ hasChildLoan, isOverdue, isEligible }) {
  if (hasChildLoan) {
    return { kind: "loan_action" };
  }
  if (isOverdue) {
    return { kind: "modal", variant: ADDITIONAL_LOAN_MODAL_VARIANT.OVERDUE };
  }
  if (!isEligible) {
    return { kind: "modal", variant: ADDITIONAL_LOAN_MODAL_VARIANT.NOT_ELIGIBLE };
  }
  return { kind: "modal", variant: ADDITIONAL_LOAN_MODAL_VARIANT.ELIGIBLE };
}
